import React, { useState, useEffect } from 'react';
import { Users, Plus, X, Search, Edit2, Trash2, UserPlus, UserMinus, Save } from 'lucide-react';
import { collaborationService } from '../../services/collaborationService';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';

const GroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    thread_type: 'group',
    participants: []
  });

  useEffect(() => {
    loadGroups();
    loadEmployees();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await collaborationService.getMessageThreads({ 
        thread_type: 'group',
        limit: 100 
      });
      
      console.log('Groups API response:', response);
      
      // Handle different response structures
      let groupsList = [];
      if (response?.data?.data?.threads) {
        groupsList = response.data.data.threads;
      } else if (response?.data?.threads) {
        groupsList = response.data.threads;
      } else if (Array.isArray(response?.data)) {
        groupsList = response.data;
      }
      
      setGroups(groupsList);
      console.log('Groups loaded:', groupsList);
    } catch (error) {
      console.error('Error loading groups:', error);
      console.error('Error response:', error.response);
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await collaborationService.getStaffList({ limit: 1000 });
      console.log('Staff API response:', response);
      
      // Handle different response structures
      let staffList = [];
      if (response?.data?.data?.staff) {
        staffList = response.data.data.staff;
      } else if (response?.data?.staff) {
        staffList = response.data.staff;
      } else if (Array.isArray(response?.data)) {
        staffList = response.data;
      }
      
      setEmployees(staffList);
      console.log('Employees loaded:', staffList);
    } catch (error) {
      console.error('Error loading employees:', error);
      console.error('Error response:', error.response);
      toast.error('Failed to load employees');
    }
  };

  const handleCreateGroup = async () => {
    if (!formData.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    if (formData.participants.length < 2) {
      toast.error('Please select at least 2 members for the group');
      return;
    }

    try {
      setLoading(true);
      
      const groupData = {
        name: formData.name,
        description: formData.description,
        thread_type: 'group',
        participants: formData.participants
      };

      console.log('Creating group with data:', groupData);
      
      const response = await collaborationService.createMessageThread(groupData);
      console.log('Create group response:', response);
      
      toast.success('Group created successfully');
      setShowCreateModal(false);
      resetForm();
      
      // Force immediate reload
      await loadGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.message || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!selectedGroup) return;

    if (!formData.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    try {
      setLoading(true);
      
      await collaborationService.updateMessageThread(selectedGroup.id, {
        title: formData.name,
        description: formData.description
      });
      
      toast.success('Group updated successfully');
      setShowEditModal(false);
      resetForm();
      await loadGroups();
    } catch (error) {
      console.error('Error updating group:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.message || 'Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Are you sure you want to delete this group? This action cannot be undone.')) return;

    try {
      setLoading(true);
      
      // Archive the thread instead of deleting
      await collaborationService.updateMessageThread(groupId, {
        is_archived: true
      });
      
      toast.success('Group deleted successfully');
      await loadGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(error.response?.data?.message || 'Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = (employeeId) => {
    if (!formData.participants.includes(employeeId)) {
      setFormData(prev => ({
        ...prev,
        participants: [...prev.participants, employeeId]
      }));
    }
  };

  const handleRemoveMember = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter(id => id !== employeeId)
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      thread_type: 'group',
      participants: []
    });
    setSelectedGroup(null);
  };

  const openEditModal = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name || '',
      description: group.description || '',
      thread_type: 'group',
      participants: group.participants?.map(p => p.id) || []
    });
    setShowEditModal(true);
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    const email = emp.email?.toLowerCase() || '';
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  const getSelectedEmployees = () => {
    return employees.filter(emp => formData.participants.includes(emp.id));
  };

  const getAvailableEmployees = () => {
    return filteredEmployees.filter(emp => !formData.participants.includes(emp.id));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Group Management</h1>
          <p className="text-gray-600">Create and manage collaboration groups</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Group
        </Button>
      </div>

      {/* Groups List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Groups ({groups.length})
          </h3>
        </div>

        {loading && groups.length === 0 ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading groups...</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No groups yet</h3>
            <p className="text-gray-600 mb-4">Create your first group to start collaborating</p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Group
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groups.map((group) => (
              <div key={group.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      <Users className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{group.name}</h4>
                      {group.description && (
                        <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                      )}
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                        <span>{group.participants?.length || 0} members</span>
                        <span>•</span>
                        <span>Created {new Date(group.created_at).toLocaleDateString()}</span>
                      </div>
                      {group.participants && group.participants.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-2">Members:</p>
                          <div className="flex flex-wrap gap-2">
                            {group.participants.slice(0, 5).map((participant) => (
                              <span
                                key={participant.id}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                              >
                                {participant.first_name} {participant.last_name}
                              </span>
                            ))}
                            {group.participants.length > 5 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                                +{group.participants.length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(group)}
                      className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100"
                      title="Edit group"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                      title="Delete group"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Group</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Group Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter group name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter group description"
                />
              </div>

              {/* Members Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Members <span className="text-red-500">*</span> (Select at least 2)
                </label>
                
                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Search employees..."
                  />
                </div>

                {/* Selected Members */}
                {getSelectedEmployees().length > 0 && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Selected Members ({getSelectedEmployees().length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {getSelectedEmployees().map((emp) => (
                        <span
                          key={emp.id}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700"
                        >
                          {emp.first_name} {emp.last_name}
                          <button
                            onClick={() => handleRemoveMember(emp.id)}
                            className="ml-2 text-primary-600 hover:text-primary-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Employees */}
                <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                  {getAvailableEmployees().length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchTerm ? 'No employees found' : 'All employees selected'}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {getAvailableEmployees().map((emp) => (
                        <div
                          key={emp.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                          onClick={() => handleAddMember(emp.id)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {emp.first_name?.[0]}{emp.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {emp.first_name} {emp.last_name}
                              </p>
                              <p className="text-xs text-gray-500">{emp.email}</p>
                            </div>
                          </div>
                          <UserPlus className="h-4 w-4 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateGroup}
                  disabled={loading || !formData.name.trim() || formData.participants.length < 2}
                >
                  {loading ? 'Creating...' : 'Create Group'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditModal && selectedGroup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Group</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Group Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter group name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter group description"
                />
              </div>

              {/* Members Management */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Members
                </label>
                
                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Search employees..."
                  />
                </div>

                {/* Selected Members */}
                {getSelectedEmployees().length > 0 && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Current Members ({getSelectedEmployees().length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {getSelectedEmployees().map((emp) => (
                        <span
                          key={emp.id}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700"
                        >
                          {emp.first_name} {emp.last_name}
                          <button
                            onClick={() => handleRemoveMember(emp.id)}
                            className="ml-2 text-primary-600 hover:text-primary-800"
                          >
                            <UserMinus className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Employees to Add */}
                <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                  {getAvailableEmployees().length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchTerm ? 'No employees found' : 'All employees are already members'}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {getAvailableEmployees().map((emp) => (
                        <div
                          key={emp.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                          onClick={() => handleAddMember(emp.id)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {emp.first_name?.[0]}{emp.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {emp.first_name} {emp.last_name}
                              </p>
                              <p className="text-xs text-gray-500">{emp.email}</p>
                            </div>
                          </div>
                          <UserPlus className="h-4 w-4 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateGroup}
                  disabled={loading || !formData.name.trim()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagement;
